import React, { useState, useEffect } from 'react';
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Link
} from 'react-router-dom';
import { Navbar, Container, Nav, Button, NavDropdown, Spinner, Badge } from 'react-bootstrap';
import './App.css';
import { scoreDraftByLineup } from './scoring';

import Home from './components/Home';
import LeagueJoin from './components/LeagueJoin';
import DraftPage from './components/DraftPage';
import RulesPage from './components/RulesPage';
import ScoringPage from './components/ScoringPage';
import PlayerSwaps from './components/PlayerSwaps';
import AdminScoring from './components/AdminScoring';
import LeagueStandings from './components/LeagueStandings';
import AdminLogin from './components/AdminLogin';
import AdminLayout from './components/AdminLayout';
import AdminDashboard from './components/AdminDashboard';
import AdminUserManagement from './components/AdminUserManagement';
import CastawayDetail from './components/CastawayDetail';
import AdminClearScores from './components/AdminClearScores';
import SurvivorPointTotals from './components/SurvivorPointTotals';
import AdminLeagueManagement from './components/AdminLeagueManagement';
import AdminDraftEdit from './components/AdminDraftEdit';
import AdminResetPassword from './components/AdminResetPassword';
import AdminCastawayManagement from './components/AdminCastawayManagement';
import ReportProblem from './components/ReportProblem';
import EliminationPick from './components/EliminationPick';
import AdminEmail from './components/AdminEmail';
import AdminEliminationPicks from './components/AdminEliminationPicks';
import AdminPlayerSwaps from './components/AdminPlayerSwaps';
import AdminStandingsEmail from './components/AdminStandingsEmail';
import AdminEpisodeManagement from './components/AdminEpisodeManagement';
import AdminMockDraft from './components/AdminMockDraft';
import SeasonHighlights from './components/SeasonHighlights';
import AuthLogin, { UserInfo } from './components/AuthLogin';
import { useCallback } from 'react';
import ForgotPassword from './components/ForgotPassword';
import ResetPassword from './components/ResetPassword';
import WeeklyLineup from './components/WeeklyLineup';

interface FoundUser {
  userName: string;
  survivorName: string;
  realName: string;
  email: string;
  leagueId: string;
  paymentMethod: string;
  season: number;
  score?: number;
  [key:string]: any;
}

interface League {
  id: string;
  name: string;
  season: number;
}

interface Draft {
  team: number[];
  originalTeam?: number[];
  swapUsed?: boolean;
  swapHistory?: any[]; // Simplified for now
  swapEffectiveEpisode?: number;
}

console.warn('[App.tsx] Module loaded');

const App: React.FC = () => {
  const [userInfo, setUserInfo] = useState<UserInfo | null>(() => {
    const currentSeason = parseInt(localStorage.getItem('selectedSeason') || '50');
    const userKey = `fantasyUser_${currentSeason}`;
    const storedUser = localStorage.getItem(userKey);
    if (storedUser) {
      try {
        return JSON.parse(storedUser);
      } catch (e) {
        // fall back to global
      }
    }
    const globalUser = localStorage.getItem('fantasyUser');
    if (globalUser) {
      try {
        return JSON.parse(globalUser);
      } catch (e) {
        return null;
      }
    }
    return null;
  });
  const [selectedSeason, setSelectedSeason] = useState<number>(() => parseInt(localStorage.getItem('selectedSeason') || '50'));
  const [availableSeasons, setAvailableSeasons] = useState<number[]>([]);
  const [isReady, setIsReady] = useState(false);
  const [userEntries, setUserEntries] = useState<FoundUser[]>([]);
  const [leagues, setLeagues] = useState<Record<string, League>>({});
  const [leagueDetails, setLeagueDetails] = useState<Record<string, { drafts: any, scores: any, eliminationPicks: any }>>({});
  const [isLoadingEntries, setIsLoadingEntries] = useState<boolean>(!!userInfo);

  const handleStorageChange = useCallback(() => {
    const currentSeason = parseInt(localStorage.getItem('selectedSeason') || '50');
    const userKey = `fantasyUser_${currentSeason}`;
    const storedUser = localStorage.getItem(userKey);
    setSelectedSeason(currentSeason);
    console.warn(`[App Debug] Storage Change - Season: ${currentSeason}, Key: ${userKey}, User Found: ${!!storedUser}`);
    
    if (storedUser) {
      try {
        const parsed = JSON.parse(storedUser);
        // Optimization: Only update state if the user object has actually changed to prevent render loops
        setUserInfo(prev => {
          if (JSON.stringify(prev) === JSON.stringify(parsed)) return prev;
          return parsed;
        });
        return;
      } catch (e) {
        console.error('[App Debug] JSON Parse Error', e);
        localStorage.removeItem(userKey);
      }
    }

    // Keep user logged in via global account session across season switches
    const globalUser = localStorage.getItem('fantasyUser');
    if (globalUser) {
      try {
        const parsedGlobal = JSON.parse(globalUser);
        setUserInfo(prev => {
          if (JSON.stringify(prev) === JSON.stringify(parsedGlobal)) return prev;
          return parsedGlobal;
        });
        return;
      } catch (e) {
        console.error('[App Debug] Global fantasyUser Parse Error', e);
      }
    }

    setUserInfo(null);
  }, []);

  const calculateScore = (castawayId: number, allScores: any, episodeRange?: { start?: number, end?: number }): number => {
    let totalScore = 0;
    Object.entries(allScores).forEach(([episode, episodeScores]: [string, any]) => {
      const episodeNum = parseInt(episode.replace('episode', ''), 10);
      if (episodeNum === 1) return; // Episode 1 is non-scoring tracking
      if (episodeRange) {
        if (episodeRange.start && episodeNum < episodeRange.start) return;
        if (episodeRange.end && episodeNum > episodeRange.end) return;
      }
      const scores = episodeScores[castawayId];
      if (scores) {
        totalScore += scores.tribeImmunityWins * 1;
        totalScore += scores.tribeRewardWins * 1;
        totalScore += scores.individualRewardWins * 2;
        totalScore += scores.rightSideOfVote * 1;
        totalScore += scores.individualImmunityWins; // This is already points
        totalScore += scores.votesAgainst * -0.5;
        totalScore += scores.idolObtained * 4;
        totalScore += scores.advantageObtained * 2;
        totalScore += scores.playedIdolAdvantage * 2;
        totalScore += scores.advantageAbandoned * -2;
        totalScore += scores.idolAbandoned * -3;
        totalScore += scores.shotInDarkWin * 4;
        totalScore += (scores.coinFlipWin || 0) * 4;
  
        if (scores.finalPlacement === 1) totalScore += 25;
        if (scores.finalPlacement === 2) totalScore += 10;
        if (scores.finalPlacement === 3) totalScore += 5;
      }
    });
    return totalScore;
  };

  const fetchUserEntries = useCallback(async (email: string) => {
    if (!email) return;
    setIsLoadingEntries(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/users/by-email/${email}`);
      if (!response.ok) throw new Error('Failed to fetch user entries');

      const data = await response.json();
      const entries: FoundUser[] = data.users || [];
      const userIsAdmin = Boolean(data.isAdmin);

      if (userIsAdmin) {
        localStorage.setItem('isAdminAuthenticated', 'true');
        setUserInfo(prev => {
          if (!prev || prev.isAdmin === true) return prev;
          return { ...prev, isAdmin: true };
        });
      }

      const details: any = {};
      const standings: Record<string, { userName: string, score: number }[]> = {};

      for (const entry of entries) {
        if (details[entry.leagueId]) continue;

        const [draftsRes, scoresRes, elimPicksRes] = await Promise.all([
          fetch(`${process.env.REACT_APP_API_URL}/api/leagues/${entry.leagueId}/drafts`),
          fetch(`${process.env.REACT_APP_API_URL}/api/scores/${entry.season}`),
          fetch(`${process.env.REACT_APP_API_URL}/api/leagues/${entry.leagueId}/elimination-picks`)
        ]);

        const draftsData = await draftsRes.json();
        const scoresData = await scoresRes.json();
        const elimPicksData = await elimPicksRes.json();
        
        details[entry.leagueId] = {
          drafts: draftsData.drafts || {},
          scores: scoresData.scores || {},
          eliminationPicks: elimPicksData.eliminationPicks || {}
        };

        const leagueStandings: { userName: string, score: number }[] = [];
        for (const userName in details[entry.leagueId].drafts) {
          const draft = details[entry.leagueId].drafts[userName];
          const rosterSize = Number(entry.season) >= 51 ? 5 : 3;
          const teamScore = scoreDraftByLineup(
            draft,
            details[entry.leagueId].scores,
            (castawayId, episodeScores) => calculateScore(castawayId, { episode: episodeScores }),
            rosterSize,
          );
          
          let eliminationPoints = 0;
          const userPicks = details[entry.leagueId].eliminationPicks[userName];
          if (userPicks) {
            Object.entries(userPicks).forEach(([episode, pickedCastawayId]: [string, any]) => {
              const episodeScores = details[entry.leagueId].scores[episode];
              if (episodeScores) {
                const votedOutCastawayId = Object.keys(episodeScores).find(castawayId => episodeScores[castawayId].votedOut);
                if (votedOutCastawayId && Number(votedOutCastawayId) === pickedCastawayId) {
                  eliminationPoints += 3;
                }
              }
            });
          }
          leagueStandings.push({ userName, score: teamScore + eliminationPoints });
        }
        standings[entry.leagueId] = leagueStandings.sort((a, b) => b.score - a.score);
      }
      
      const rankedEntries = entries.map(entry => {
        const leagueRanking = standings[entry.leagueId];
        const userStanding = leagueRanking?.find(u => u.userName === entry.userName);
        const rank = leagueRanking?.findIndex(u => u.userName === entry.userName) + 1;
        return { 
          ...entry, 
          rank: rank > 0 ? rank : undefined,
          score: userStanding ? userStanding.score : 0,
          swapUsed: details[entry.leagueId]?.drafts[entry.userName]?.swapUsed || false
        };
      });

      setUserEntries(rankedEntries);
      setLeagueDetails(details);

    } catch (err) {
      console.error('Error fetching user entries:', err);
      // Don't clear entries on error to avoid flashing empty state
    } finally {
      setIsLoadingEntries(false);
    }
  }, []);

  const fetchAllLeagues = useCallback(async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/leagues`);
      if (response.ok) {
        const data = await response.json();
        const leagueMap: Record<string, League> = {};
        if (data.leagues) {
           data.leagues.forEach((l: League) => leagueMap[l.id] = l);
        }
        setLeagues(leagueMap);
      }
    } catch (error) {
      console.error("Failed to fetch leagues", error);
    }
  }, []);

  useEffect(() => {
    const fetchSeasons = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_API_URL}/api/seasons`);
        if (!response.ok) throw new Error('Failed to fetch seasons');
        const data = await response.json();
        const seasons = data.seasons || [50];
        setAvailableSeasons(seasons);
        
        const saved = localStorage.getItem('selectedSeason');
        let defaultSeason = saved ? parseInt(saved) : 0;
        const latest = Math.max(...seasons);
        
        // Default to latest if nothing saved OR if we are on the homepage
        if (!defaultSeason) {
            defaultSeason = latest;
        }
        
        setSelectedSeason(defaultSeason);
        // Only update localStorage if it's different to avoid triggering storage events unnecessarily
        if (localStorage.getItem('selectedSeason') !== defaultSeason.toString()) {
          localStorage.setItem('selectedSeason', defaultSeason.toString());
        }
      } catch (err) {
        console.error('Error fetching seasons:', err);
        if (!localStorage.getItem('selectedSeason')) {
          localStorage.setItem('selectedSeason', '50');
        }
        setSelectedSeason(parseInt(localStorage.getItem('selectedSeason')!));
      } finally {
        setIsReady(true);
      }
    };
    fetchSeasons();
    fetchAllLeagues();
  }, [fetchAllLeagues]);

  useEffect(() => {
    if (userInfo?.email) {
      fetchUserEntries(userInfo.email);
    } else {
      // If user is logged in but has no email, their session is invalid. Clear it to prevent empty state.
      if (userInfo) {
        const currentSeason = localStorage.getItem('selectedSeason') || '50';
        localStorage.removeItem(`fantasyUser_${currentSeason}`);
        setUserInfo(null);
      }
      setUserEntries([]);
      setIsLoadingEntries(false);
    }
  }, [userInfo?.email, fetchUserEntries]);
  
  useEffect(() => {
    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('seasonChanged', handleStorageChange);
    return () => {
        window.removeEventListener('storage', handleStorageChange);
        window.removeEventListener('seasonChanged', handleStorageChange);
    };
  }, [handleStorageChange]);

  const handleLogout = () => {
    const currentSeason = parseInt(localStorage.getItem('selectedSeason') || '50');
    localStorage.removeItem(`fantasyUser_${currentSeason}`);
    localStorage.removeItem('fantasyUser');
    localStorage.removeItem('survivor_saved_id');
    localStorage.removeItem('survivor_saved_pass');
    localStorage.removeItem('isAdminAuthenticated');
    setUserInfo(null);
  };

  const handleLoginSuccess = (loggedInUser: UserInfo) => {
    const currentSeason = localStorage.getItem('selectedSeason') || '50';
    if (loggedInUser.isAdmin) {
      localStorage.setItem('isAdminAuthenticated', 'true');
    }
    localStorage.setItem(`fantasyUser_${currentSeason}`, JSON.stringify(loggedInUser));
    setIsLoadingEntries(true);
    setUserInfo(loggedInUser);
  };

  const handleUserSwitch = (newUserInfo: FoundUser) => {
    // Ensure we don't save a user state without an email (fall back to current email if missing)
    const userToSave = { ...newUserInfo };
    if (!userToSave.email && userInfo?.email) {
        userToSave.email = userInfo.email;
    }

    // If the user entry specifies a season, update the app's selected season context
    if (userToSave.season) {
        if (localStorage.getItem('selectedSeason') !== userToSave.season.toString()) {
            localStorage.setItem('selectedSeason', userToSave.season.toString());
            setSelectedSeason(userToSave.season);
        }
    }
    const targetSeason = userToSave.season ? userToSave.season.toString() : (localStorage.getItem('selectedSeason') || '50');
    localStorage.setItem(`fantasyUser_${targetSeason}`, JSON.stringify(userToSave));

    if (userInfo && userInfo.userName === newUserInfo.userName && String(userInfo.leagueId) === String(newUserInfo.leagueId)) {
       if (userInfo !== newUserInfo) {
           setUserInfo(userToSave as UserInfo);
       }
       return;
    }

    setUserInfo(userToSave as UserInfo);
  };

  // Prioritize the league ID from the active userInfo to ensure Navbar links 
  // stay in sync with the user's current selection.
  const currentLeagueId = userInfo?.leagueId || 
    userEntries.find(entry => entry.season === selectedSeason)?.leagueId;

  if (!isReady) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ height: '100vh' }}>
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Loading...</span>
        </Spinner>
      </div>
    );
  }

  return (
    <Router>
      <div className="App">
        <Navbar expand="lg" variant="dark" className="custom-navbar">
          <Container fluid="lg">
            <Navbar.Brand as={Link} to="/">
              <span>🔥</span> Survivor Fantasy
            </Navbar.Brand>
            <Navbar.Toggle aria-controls="basic-navbar-nav" className="border-0 shadow-none" />
            <Navbar.Collapse id="basic-navbar-nav">
              <Nav className="me-auto align-items-lg-center">
                <Nav.Link as={Link} to="/">
                  🏠 Home
                </Nav.Link>
                {userInfo ? (
                  <NavDropdown
                    title={<span>🏝️ My Team</span>}
                    id="my-team-dropdown"
                    menuVariant="dark"
                    className="custom-dropdown"
                  >
                    <div className="custom-dropdown-menu">
                      <NavDropdown.Item as={Link} to={`/league/${currentLeagueId}`} disabled={!currentLeagueId}>
                        🏆 My League Standings
                      </NavDropdown.Item>
                      {selectedSeason >= 51 && (
                        <NavDropdown.Item as={Link} to="/lineup">
                          ⚡ Weekly Active Lineup
                        </NavDropdown.Item>
                      )}
                      <NavDropdown.Item as={Link} to={`/leagues/${currentLeagueId}/elimination-pick`} disabled={!currentLeagueId}>
                        🗳️ Weekly Elimination Pick
                      </NavDropdown.Item>
                      <NavDropdown.Item as={Link} to="/draft">
                        📋 {selectedSeason >= 51 ? 'Draft & Full Roster' : 'Draft'}
                      </NavDropdown.Item>
                      {selectedSeason < 51 && (
                        <NavDropdown.Item as={Link} to="/swaps">
                          🔄 Player Swaps
                        </NavDropdown.Item>
                      )}
                      <NavDropdown.Divider />
                      <NavDropdown.Item as={Link} to={`/mock-draft?season=${Math.max(...availableSeasons, 51)}`}>
                        🎯 Practice Mock Draft
                      </NavDropdown.Item>
                      <NavDropdown.Item as={Link} to="/survivor-scores">
                        📊 My Survivor Scores
                      </NavDropdown.Item>
                    </div>
                  </NavDropdown>
                ) : (
                  <Nav.Link as={Link} to="/join">
                    🏝️ Join/View League
                  </Nav.Link>
                )}
                <NavDropdown
                  title={<span>📜 Game Info</span>}
                  id="game-info-dropdown"
                  menuVariant="dark"
                  className="custom-dropdown"
                >
                  <div className="custom-dropdown-menu">
                    <NavDropdown.Item as={Link} to="/rules">
                      📖 Official Rules
                    </NavDropdown.Item>
                    <NavDropdown.Item as={Link} to="/scoring">
                      ⭐ Scoring Breakdown
                    </NavDropdown.Item>
                  </div>
                </NavDropdown>
              </Nav>
              <Nav className="align-items-lg-center gap-2 mt-2 mt-lg-0">
                {userInfo ? (
                  <div className="d-flex align-items-center gap-2 flex-wrap">
                    {userInfo.isAdmin ? (
                      <NavDropdown
                        title={
                          <div className="user-status-pill cursor-pointer" style={{ cursor: 'pointer' }}>
                            <span style={{ fontSize: '1rem' }}>👑</span>
                            <span className="user-status-name" title={(userInfo as any).survivorName || userInfo.userName}>
                              {(userInfo as any).survivorName || userInfo.userName}
                            </span>
                            <Badge bg="danger" className="fw-bold px-2 py-1" style={{ fontSize: '0.72rem' }}>
                              ADMIN
                            </Badge>
                            <Badge bg="warning" text="dark" className="fw-bold px-2 py-1" style={{ fontSize: '0.72rem' }}>
                              S{selectedSeason}
                            </Badge>
                          </div>
                        }
                        id="admin-user-dropdown"
                        menuVariant="dark"
                        className="custom-dropdown admin-user-dropdown"
                        align="end"
                      >
                        <div className="custom-dropdown-menu">
                          <div className="px-3 py-2 text-warning fw-bold border-bottom border-secondary mb-1 small">
                            🛠️ ADMIN CONTROLS
                          </div>
                          <NavDropdown.Item as={Link} to="/admin/dashboard">
                            ⚡ Admin Dashboard
                          </NavDropdown.Item>
                          <NavDropdown.Item as={Link} to="/admin/scoring">
                            📊 Enter Episode Scores
                          </NavDropdown.Item>
                          <NavDropdown.Item as={Link} to="/admin/users">
                            👥 Manage Users
                          </NavDropdown.Item>
                          <NavDropdown.Item as={Link} to="/admin/leagues">
                            🏝️ League Management
                          </NavDropdown.Item>
                          <NavDropdown.Item as={Link} to="/admin/email">
                            ✉️ Email Center
                          </NavDropdown.Item>
                          <NavDropdown.Item as={Link} to="/admin/edit-draft">
                            📝 Edit User Drafts
                          </NavDropdown.Item>
                          <NavDropdown.Item as={Link} to="/admin/elimination-picks">
                            🗳️ Manage Elimination Picks
                          </NavDropdown.Item>
                          <NavDropdown.Divider />
                          <NavDropdown.Item onClick={handleLogout} className="text-danger">
                            🚪 Logout
                          </NavDropdown.Item>
                        </div>
                      </NavDropdown>
                    ) : (
                      <div className="user-status-pill">
                        <span style={{ fontSize: '1rem' }}>🔥</span>
                        <span className="user-status-name" title={(userInfo as any).survivorName || userInfo.userName}>
                          {(userInfo as any).survivorName || userInfo.userName}
                        </span>
                        <Badge bg="warning" text="dark" className="fw-bold px-2 py-1" style={{ fontSize: '0.72rem' }}>
                          S{selectedSeason}
                        </Badge>
                      </div>
                    )}
                    {!userInfo.isAdmin && (
                      <Button className="btn-nav-logout" onClick={handleLogout}>
                        Logout
                      </Button>
                    )}
                  </div>
                ) : null}
                <Nav.Link as={Link} to="/report-problem" className="p-0">
                  <span className="btn-nav-problem">
                    ⚠️ Report Problem
                  </span>
                </Nav.Link>
              </Nav>
            </Navbar.Collapse>
          </Container>
        </Navbar>

        <Routes>
          <Route path="/" element={userInfo ? <Home userInfo={userInfo} userEntries={userEntries} leagues={leagues} selectedSeason={selectedSeason} currentSeason={Math.max(...availableSeasons, selectedSeason)} onUserSwitch={handleUserSwitch} isLoading={isLoadingEntries} leagueDetails={leagueDetails} /> : (
            <div className="home-banner">
              <div className="banner-overlay">
                <Container className="d-flex flex-column justify-content-center align-items-center banner-content-container">
                  <h1 className="banner-title mb-4 text-center">Survivor Fantasy Pool</h1>
                  <div style={{ maxWidth: '480px', width: '100%' }}>
                    <AuthLogin onLoginSuccess={handleLoginSuccess} />
                    
                    <div className="text-center mt-3 mb-4">
                      <Link to="/forgot-password" style={{ color: 'rgba(255,255,255,0.85)', textDecoration: 'underline', textShadow: '0px 1px 2px rgba(0,0,0,0.8)', fontSize: '0.95rem' }}>
                        Forgot Password?
                      </Link>
                    </div>

                    <div className="d-flex justify-content-center gap-2 flex-wrap">
                      <Link to="/join" className="btn rounded-pill px-3 py-2 quick-link-btn text-decoration-none">
                        🏝️ Join a League
                      </Link>
                      <Link to="/mock-draft" className="btn rounded-pill px-3 py-2 quick-link-btn text-decoration-none">
                        🎯 Practice Mock Draft
                      </Link>
                      <Link to="/rules" className="btn rounded-pill px-3 py-2 quick-link-btn text-decoration-none">
                        📜 View Rules
                      </Link>
                    </div>
                  </div>
                </Container>
              </div>
            </div>
          )} />
          <Route path="/join" element={<LeagueJoin setUserInfo={setUserInfo} userInfo={userInfo} />} />
          <Route path="/draft" element={<DraftPage />} />
          <Route path="/lineup" element={<WeeklyLineup />} />
          <Route path="/mock-draft" element={<AdminMockDraft userMode={true} />} />
          <Route path="/rules" element={<RulesPage />} />
          <Route path="/scoring" element={<ScoringPage />} />
          <Route path="/swaps" element={<PlayerSwaps />} />
          <Route path="/league/:leagueId" element={<LeagueStandings />} />
          <Route path="/league/:leagueId/highlights" element={<SeasonHighlights />} />
          <Route path="/league/:leagueId/castaway/:castawayId" element={<CastawayDetail />} />
          <Route path="/leagues/:leagueId/elimination-pick" element={<EliminationPick />} />
          <Route path="/survivor-scores" element={<SurvivorPointTotals />} />
          <Route path="/report-problem" element={<ReportProblem />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          
          {/* Admin Routes */}
          <Route path="/admin/login" element={<AdminLogin />} />
          <Route path="/admin/reset-password" element={<AdminResetPassword />} />
          <Route element={<AdminLayout />}>
            <Route path="/admin/dashboard" element={<AdminDashboard />} />
            <Route path="/admin/castaways" element={<AdminCastawayManagement />} />
            <Route path="/admin/leagues" element={<AdminLeagueManagement />} />
            <Route path="/admin/scoring" element={<AdminScoring />} />
            <Route path="/admin/users" element={<AdminUserManagement />} />
            <Route path="/admin/clear-scores" element={<AdminClearScores />} />
            <Route path="/admin/edit-draft" element={<AdminDraftEdit />} />
            <Route path="/admin/mock-draft" element={<AdminMockDraft />} />
            <Route path="/admin/email" element={<AdminEmail />} />
            <Route path="/admin/elimination-picks" element={<AdminEliminationPicks />} />
            <Route path="/admin/player-swaps" element={<AdminPlayerSwaps />} />
            <Route path="/admin/episode-deadlines" element={<AdminEpisodeManagement />} />
            <Route path="/admin/standings-email" element={<AdminStandingsEmail />} />
          </Route>
        </Routes>

        <footer className="text-center mt-5 mb-3 footer-custom">
          <p>&copy; 2025 Survivor Fantasy Pool | <Link to="/admin/login" className="admin-login-link">Admin Login</Link></p>
        </footer>
      </div>
    </Router>
  );
}

export default App;